import * as React from "react";
import {
    Box,
    Stack,
    Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    TablePagination,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";

const sampleRows = [
    {
        id: 1,
        accession_no: "AY220558",
        alignment_gene_length: null,
        coverage: null,
        identity: null,
        phenotype: "amikacin, isepamicin, netilmicin, tobramycin",
        resistance_gene: "armA",
    },
];

// Escape RFC4180-ish: comillas dobles, comas, saltos de linea
const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    const mustQuote = /[",\n\r]/.test(str);
    const escaped = str.replace(/"/g, '""');
    return mustQuote ? `"${escaped}"` : escaped;
};

const normalizeCell = (v) => {
    if (Array.isArray(v)) return v.join(", ");
    return v;
};

const objectToCSV = ({ columns = [], rows = [] }) => {
    const headers = columns.map((c) => escapeCSV(c.label ?? c.key ?? ""));
    const lines = [headers.join(",")];

    for (const r of rows) {
        const row = columns.map((c) => {
            const key = c.key;
            const value = key ? normalizeCell(r?.[key]) : "";
            return escapeCSV(value);
        });
        lines.push(row.join(","));
    }

    return lines.join("\r\n");
};

const rowsArrayToCSV = (rows = []) => {
    const keys = rows[0] ? Object.keys(rows[0]) : [];
    const headers = keys.map(escapeCSV);
    const lines = [headers.join(",")];

    for (const r of rows) {
        const line = keys.map((k) => escapeCSV(normalizeCell(r?.[k])));
        lines.push(line.join(","));
    }

    return lines.join("\r\n");
};

const safeFilename = (name) => {
    return String(name || "resistance_genes")
        .trim()
        .replace(/[^\w\-]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase();
};

function ToolbarCSV({ data, rows }) {
    const csv = React.useMemo(() => {
        if (data?.columns && data?.rows) return objectToCSV(data);
        return rowsArrayToCSV(rows ?? []);
    }, [data, rows]);

    const filename = React.useMemo(() => {
        const base = safeFilename(data?.title) || "resistance_genes";
        return `${base}.csv`;
    }, [data?.title]);

    const copyCSV = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(csv);
                return;
            }
        } catch { }

        const ta = document.createElement("textarea");
        ta.value = csv;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
    };

    const downloadCSV = () => {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Tooltip title="Copiar CSV">
                <IconButton size="small" onClick={copyCSV}>
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Descargar CSV">
                <IconButton size="small" onClick={downloadCSV}>
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

export default function ResistanceGenesTableMUI({ rows = sampleRows }) {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (_e, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const paginatedRows = rows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box sx={{ width: "100%" }}>
            <ToolbarCSV rows={rows} />

            <Paper
                elevation={2}
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height: 700,
                    width: "100%",
                }}
            >
                <TableContainer
                    sx={{
                        flex: 1,
                        width: "100%",
                        overflowX: "auto",
                        overflowY: "auto",
                    }}
                >
                    <Table
                        stickyHeader
                        aria-label="resistance genes"
                        sx={{
                            minWidth: 1200,
                            tableLayout: "fixed",
                            "& th": {
                                bgcolor: "#000",
                                color: "#fff",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                            },
                            "& td, & th": {
                                borderColor: "divider",
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ width: 220, minWidth: 220 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Resistance gene
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 120, minWidth: 120 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Identity
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 220, minWidth: 220 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Alignment Length/Gene Length
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 130, minWidth: 130 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Coverage
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 380, minWidth: 380 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Phenotype
                                    </Typography>
                                </TableCell>

                                <TableCell align="center" sx={{ width: 160, minWidth: 160 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Accession no.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginatedRows.map((r, idx) => {
                                const key =
                                    r?.id ??
                                    r?.accession_no ??
                                    `${r?.resistance_gene ?? "gene"}-${page * rowsPerPage + idx}`;

                                return (
                                    <TableRow key={key}>
                                        <TableCell align="center">
                                            <Typography variant="body2" fontWeight={700}>
                                                {r?.resistance_gene ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {r?.identity ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {r?.alignment_gene_length ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {r?.coverage ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {Array.isArray(r?.phenotype)
                                                    ? r.phenotype.join(", ")
                                                    : r?.phenotype ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {r?.accession_no ?? "—"}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    sx={{ mt: "auto" }}
                    count={rows.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>
        </Box>
    );
}